/**
 * UploadQueueItem Component
 * Individual file in upload queue with progress tracking
 */

import { forwardRef } from 'react';
import { Card, Group, Stack, Text, ActionIcon, Progress, Badge } from '@/components/ui';
import { IconX, IconRefresh, IconCheck, IconAlertCircle } from '@tabler/icons-react';
import { formatFileSize } from '../../utils/formatting';
import { FileTypeIcon } from './FileTypeIcon';
import type { GradientVariant } from '@/components/ccms-components/types';
import { usePreferencesStore } from '../../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../../hooks/useAccessibilityStyles';

export interface UploadQueueFile {
  id: string;
  fileName: string;
  mimeType: string;
  fileSize: number;
  progress: number; // 0-100
  status: 'pending' | 'uploading' | 'processing' | 'completed' | 'failed';
  error?: string;
}

export interface UploadQueueItemProps {
  /** File being uploaded */
  file: UploadQueueFile;
  /** Callback to cancel upload */
  onCancel?: (fileId: string) => void;
  /** Callback to retry failed upload */
  onRetry?: (fileId: string) => void;
  /** Gradient variant for progress bar */
  gradientVariant?: GradientVariant;
  /** Show file icon */
  showIcon?: boolean;
  /** Additional className */
  className?: string;
}

/**
 * Upload queue item component showing file upload progress
 *
 * @example
 * ```tsx
 * <UploadQueueItem
 *   file={{
 *     id: 'upload-123',
 *     fileName: 'document.pdf',
 *     mimeType: 'application/pdf',
 *     fileSize: 1024000,
 *     progress: 65,
 *     status: 'uploading'
 *   }}
 *   onCancel={(id) => cancelUpload(id)}
 *   onRetry={(id) => retryUpload(id)}
 * />
 * ```
 */
export const UploadQueueItem = forwardRef<HTMLDivElement, UploadQueueItemProps>(
  (props, ref) => {
    const {
      file,
      onCancel,
      onRetry,
      gradientVariant = 'primary',
      showIcon = true,
      className,
    } = props;

    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    const handleCancel = () => {
      onCancel?.(file.id);
    };

    const handleRetry = () => {
      onRetry?.(file.id);
    };

    // Status colors and icons
    const getStatusColor = (): string => {
      switch (file.status) {
        case 'completed':
          return 'green';
        case 'failed':
          return 'red';
        case 'uploading':
        case 'processing':
          return 'blue';
        default:
          return 'gray';
      }
    };

    const getStatusIcon = () => {
      switch (file.status) {
        case 'completed':
          return <IconCheck size={16} />;
        case 'failed':
          return <IconAlertCircle size={16} />;
        default:
          return null;
      }
    };

    const getStatusLabel = (): string => {
      switch (file.status) {
        case 'pending':
          return 'Pending';
        case 'uploading':
          return 'Uploading';
        case 'processing':
          return 'Processing';
        case 'completed':
          return 'Completed';
        case 'failed':
          return 'Failed';
        default:
          return '';
      }
    };

    return (
      <Card ref={ref} variant="outlined" p="sm" className={className}>
        <Stack gap="xs">
          {/* File Info Row */}
          <Group justify="space-between" align="flex-start">
            <Group gap="sm" style={{ flex: 1, minWidth: 0 }}>
              {showIcon && (
                <div style={{ flexShrink: 0 }}>
                  <FileTypeIcon mimeType={file.mimeType} size="sm" />
                </div>
              )}

              <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                <Text size="sm" fw={500} lineClamp={1} style={accessibilityStyles}>
                  {file.fileName}
                </Text>
                <Group gap="xs">
                  <Text size="xs" c="dimmed" style={accessibilityStyles}>
                    {formatFileSize(file.fileSize)}
                  </Text>
                  <Badge size="xs" variant="light" color={getStatusColor()}>
                    {getStatusLabel()}
                  </Badge>
                </Group>
              </Stack>
            </Group>

            {/* Actions */}
            <Group gap={4} style={{ flexShrink: 0 }}>
              {file.status === 'failed' && onRetry && (
                <ActionIcon
                  variant="subtle"
                  size="sm"
                  onClick={handleRetry}
                  aria-label="Retry upload"
                >
                  <IconRefresh size={16} />
                </ActionIcon>
              )}
              {file.status !== 'completed' && onCancel && (
                <ActionIcon
                  variant="subtle"
                  gradientVariant="danger"
                  size="sm"
                  onClick={handleCancel}
                  aria-label="Cancel upload"
                >
                  <IconX size={16} />
                </ActionIcon>
              )}
              {file.status === 'completed' && getStatusIcon()}
            </Group>
          </Group>

          {/* Progress Bar */}
          {(file.status === 'uploading' || file.status === 'processing') && (
            <Stack gap={4}>
              <Progress
                value={file.progress}
                size="sm"
                gradientVariant={gradientVariant}
                animated
                aria-label={`Upload progress: ${file.progress}%`}
              />
              <Group justify="space-between">
                <Text size="xs" c="dimmed" style={accessibilityStyles}>
                  {file.status === 'processing' ? 'Processing...' : 'Uploading...'}
                </Text>
                <Text size="xs" c="dimmed" fw={500} style={accessibilityStyles}>
                  {file.progress}%
                </Text>
              </Group>
            </Stack>
          )}

          {/* Error Message */}
          {file.status === 'failed' && file.error && (
            <Text size="xs" c="red" style={accessibilityStyles}>
              {file.error}
            </Text>
          )}
        </Stack>
      </Card>
    );
  }
);

UploadQueueItem.displayName = 'UploadQueueItem';

export default UploadQueueItem;
